import { useState, useEffect } from 'react';
import { Space, Table, Skeleton, Popconfirm, message, Popover } from 'antd';
import type { ColumnsType } from 'antd/lib/table';
import { AxiosError } from 'axios';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { PlusOutlined } from '@ant-design/icons';

import useTranslation from 'customHooks/translate';
import { BANK_DATA } from 'events/types';
import styled from 'styled-components';
import AddNewBankAccountForm from 'pages/Dialogs/AddNewBankAccountForm';
import { deleteBank, getBanks } from 'services/banks';
import { toastError } from 'services/helpers/getError';
import { BanksDataType } from 'types';

const Banks = () => {
  const queryClient = useQueryClient();
  const translation = useTranslation();
  const [openModal, setOpenModal] = useState(false);
  const [bankToEdit, setBankToEdit] = useState<BanksDataType>();

  const handleOpenModal = () => {
    setBankToEdit(undefined);
    setOpenModal(true);
  };

  const handleCloseModal = () => setOpenModal(false);

  const handleDelete = async (id: number) => {
    try {
      await deleteBank(id);
      message.success(`${translation.messages.successDeleted}`);
      refetch();
      queryClient.invalidateQueries({
        queryKey: [BANK_DATA],
        refetchType: 'all',
      });
    } catch (error) {
      toastError(error);
    }
  };

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const columns: ColumnsType<BanksDataType> = [
    {
      title: <span>{translation.bankAdd.Name}</span>,
      dataIndex: 'bankName',
      key: 'bankName',
      width: 300,
      align: 'center',
    },
    {
      title: (
        <span>{translation.bankAdd.Account}</span>
      ),
      dataIndex: 'iban',
      key: 'iban',
      width: 300,
      align: 'center',
    },
    {
      title: <span>{translation.bankAdd.Swift}</span>,
      dataIndex: 'swift',
      key: 'swift',
      width: 300,
      align: 'center',
    },
    {
      title: <span>{translation.bankAdd.Code}</span>,
      width: 300,
      key: 'bankCode',
      align: 'center',
      render: (_, record) => <p>{record.bankCode}</p>,
    },
    {
      title: (
        <span>
          {translation.bankAdd.DefaultCurrency}
        </span>
      ),
      dataIndex: 'currencyId',
      key: 'currencyId',
      align: 'center',
      width: 300,
    },
    {
      title: <span>{translation.action.Action}</span>,
      dataIndex: 'actions',
      align: 'center',
      key: 'actions',
      width: 200,
      render: (_, record) =>
        isMobile ? (
          <Popover
            placement="bottom"
            trigger="click"
            content={
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '20px',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <EditDeleteIcon
                  src="assets/edit-icon.svg"
                  alt="Edit"
                  onClick={() => {
                    setBankToEdit(record);
                    setOpenModal(true);
                  }}
                />
                <Popconfirm
                  title={translation.DeleteItem}
                  onConfirm={() => handleDelete(record.id!)}
                  okText="Yes"
                  placement="left"
                  cancelText="No"
                >
                  <EditDeleteIcon src="assets/delete-icon.svg" alt="Delete" />
                </Popconfirm>
              </div>
            }
          >
            <MoreVertIcon
              style={{
                fontSize: '29px',
                border: '1px solid #292d32bf',
                borderRadius: '50%',
                padding: '4px',
              }}
            />
          </Popover>
        ) : (
          <Space size="middle">
            <div style={{ cursor: 'pointer' }}>
              <img
                className="edit"
                src="assets/edit-icon.svg"
                alt="Edit"
                onClick={() => {
                  setBankToEdit(record);
                  setOpenModal(true);
                }}
              />
            </div>
            <Popconfirm
              title={translation.DeleteItem}
              onConfirm={() => handleDelete(record.id!)}
              okText="Yes"
              cancelText="No"
            >
              <div style={{ cursor: 'pointer' }}>
                <img
                  className="delete"
                  src="assets/delete-icon.svg"
                  alt="Delete"
                />
              </div>
            </Popconfirm>
          </Space>
        ),
    },
  ];

  const { status, data, refetch, isLoading, isFetching } = useQuery<
    any,
    AxiosError,
    any[]
  >({
    queryKey: [BANK_DATA],
    queryFn: getBanks,
    select: (response) => response.data.data.map((d) => ({ ...d, key: d.id })),
    keepPreviousData: true,
    refetchOnMount: 'always',
  });

  return (
    <div>
      <div className="mb-[20px] flex w-full flex-wrap items-center justify-between gap-[10px]">
        <p className="text-[20px] font-medium text-[#1D1D1D] md:text-[24px]">
          {translation.bankAdd.BankAccounts}
        </p>
        <button
          onClick={handleOpenModal}
          className="flex w-auto w-full w-max items-center gap-[8px] rounded-[6px] rounded-[6px] bg-[#466afb] px-[16px] py-[8px] text-[16px] font-normal leading-[16px] text-white md:px-[24px] md:py-[12px]"
        >
          <PlusOutlined />
          <p className="hidden md:block">{translation.bankAdd.HeaderAddBank}</p>
        </button>
      </div>

      {status === 'loading' ? (
        <Skeleton active />
      ) : (
        <STableWrapper>
          <Table
            sticky={{
              offsetHeader: 0,
            }}
            bordered
            className="custom-bordered"
            scroll={{ x: 'max-content' }}
            dataSource={data}
            columns={columns}
            loading={isLoading || isFetching}
            pagination={false}
            rowClassName={(_, i) =>
              i % 2 === 0 ? 'bg-[#466AFB08]' : 'bg-white'
            }
          />
        </STableWrapper>
      )}

      <AddNewBankAccountForm
        open={openModal}
        handleClose={handleCloseModal}
        bankToEdit={bankToEdit}
      />
    </div>
  );
};

export default Banks;

const STableWrapper = styled.div`
  margin-top: 24px;
  height: 500px;
  overflow: auto;
`;

const EditDeleteIcon = styled.img`
  cursor: pointer;
  width: 24px;
  height: 24px;
`;
